import { formatISO } from 'date-fns';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  useEditContract,
  useGetContractDetails,
  useResolveContractUnitMismatch,
} from '@/modules/workforce/Contracts/apiHooks';
import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import ContractDate from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractDetailsInputs/ContractDate';
import DailyBased from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractTypeInputs/DailyBased';
import UnitBase from '@/modules/workforce/Contracts/components/CreateAndEdit/Steps/ContractTypeInputs/UnitBase';
import { useContractDetails } from '@/modules/workforce/Contracts/components/Details/logic';
import { IContracts } from '@/modules/workforce/Contracts/types';
import { Button, Loading, Typography } from '@/shared/components/atoms';
import Alert from '@/shared/components/atoms/Alert';
import IconWithTitleDescription from '@/shared/components/atoms/IconWithTItleDescription';
import ScrollableWithShadow from '@/shared/components/atoms/ScrollableWithShadow';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import ReviewCard from '@/shared/components/molecules/MultiStepForm/ReviewCard';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const EditContractForm: React.FC<{ supplierAssignmentId: number }> = ({
  supplierAssignmentId,
}) => {
  const { t } = useTranslation();

  const [selectedAsset] = useStore((store) => store.asset);
  const { openModal, closeModal } = useModal();
  const { replace, pathname } = useRouter();

  const {
    getEditMutation,
    getResolveMismatchMutation,
    parseTimeToDate,
    getContractGeneralInfoCardItems,
  } = useCreateEditContractUtils();
  const { checkUnitMismatch } = useContractDetails();

  const { mutate: edit, isLoading: isEditingContract } = useEditContract();
  const { mutate: resolveMismatch, isLoading: isEditingMismatch } =
    useResolveContractUnitMismatch();
  const { data: contract, isLoading: isLoadingContract } =
    useGetContractDetails({
      entityId: selectedAsset?.id,
      supplierAssignmentId,
    });

  const hasUnitMismatch = contract
    ? checkUnitMismatch(
        contract.contractStatus,
        contract.dailyWage,
        contract.pricingUnits
      )
    : false;

  const methods = useFormContext<IContracts.IAddContractSchema>();
  const { setValue, resetField } = methods;

  const onSuccess = () => {
    closeModal(2);
    toast.success(t('edit-contract-success'));
    if (
      contract?.status === 'Active' &&
      !hasUnitMismatch &&
      !!contract?.contractStatus
    )
      replace({
        pathname,
        query: {
          'contract-type': 2,
        },
      });
  };

  const handleEdit = (values: IContracts.IAddContractSchema) =>
    hasUnitMismatch
      ? resolveMismatch(
          {
            supplierAssignmentId,
            ...getResolveMismatchMutation(values),
          },
          { onSuccess }
        )
      : edit(
          {
            supplierAssignmentId,
            todayDate: formatISO(new Date()),
            ...getEditMutation(values),
          },
          { onSuccess }
        );

  const onSubmit: SubmitHandler<IContracts.IAddContractSchema> = (values) => {
    if (contract?.status === 'Active' || !contract?.contractStatus)
      openModal(
        <ConfirmationModal
          header={t(
            contract?.contractStatus
              ? 'edit-contract-confirmation'
              : 'update-contract-value'
          )}
          variant='warning'
          message={t(
            contract?.contractStatus
              ? 'edit-contract-warning'
              : 'update-previous-payments'
          )}
          onConfirm={() => handleEdit(values)}
          confirmText={t(
            contract?.contractStatus ? 'edit-contract' : 'save-edits'
          )}
        />
      );
    else handleEdit(values);
  };

  useEffect(() => {
    if (contract) {
      const extra = contract.intervals?.find((i) => i.type === 'extra');
      const dailyBasedStart = contract.intervals?.[0]?.startTime;
      const dailyBasedEnd = contract.intervals?.[0]?.endTime;
      setValue('startDate', new Date(contract.startDate.replace(/Z$/, '')));
      setValue('endDate', new Date(contract.endDate.replace(/Z$/, '')));
      setValue('dailyWage', contract.dailyWage);
      setValue('status', contract.status);
      if (contract.dailyWage)
        resetField('dailyBased', {
          defaultValue: {
            hasExtraTime: Boolean(extra),
            rate: contract.dailyRate || 0,
            startTime: dailyBasedStart
              ? (parseTimeToDate(dailyBasedStart) as Date)
              : undefined,
            endTime: dailyBasedEnd
              ? (parseTimeToDate(dailyBasedEnd) as Date)
              : undefined,
            extraHoursPricing: [
              {
                hourPrice: extra?.hourPrice,
                startTime: extra?.startTime,
                endTime: extra?.endTime,
              },
            ],
          },
        });
      else {
        setValue(
          'unitBased',
          contract.pricingUnits?.map((item) => ({
            // used to edit contract with mismatch
            id: hasUnitMismatch ? item.id : undefined,
            unitId: item.unitId,
            unitName: item.unit ?? undefined,
            unitPrice: item.price,
            unitEditable: contract.status === 'Upcoming' && !hasUnitMismatch,
            expanded: hasUnitMismatch && !item.price,
          }))
        );
        setValue('hasUnitMismatch', hasUnitMismatch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  return (
    <form
      className='mx-auto h-full w-1/2 pt-20'
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <div className='flex h-full flex-col justify-between'>
        <div>
          <Typography variant='h1' weight='bold' className='mb-40'>
            {t('contract-details')}
          </Typography>
          <ScrollableWithShadow>
            {isLoadingContract && !contract ? (
              <Loading />
            ) : contract ? (
              <>
                {checkUnitMismatch(
                  contract.contractStatus,
                  contract.dailyWage,
                  contract.pricingUnits
                ) && (
                  <Alert
                    className='mb-16'
                    type='danger'
                    message={t('edit-unit-mismatch')}
                  />
                )}
                <div className='mb-16 flex flex-col gap-24 pb-56'>
                  <ReviewCard header={t('general-info')} borderedHeader>
                    {getContractGeneralInfoCardItems(contract).map(
                      (item, idx) => (
                        <IconWithTitleDescription
                          key={`${item.title}-${idx}`}
                          item={item}
                        />
                      )
                    )}
                  </ReviewCard>
                  <ContractDate />
                  {contract.dailyWage === 1 ? <DailyBased /> : <UnitBase />}
                </div>
              </>
            ) : null}
          </ScrollableWithShadow>
        </div>
        <Button
          type='submit'
          size='full'
          className='flex h-56 items-center justify-center gap-8 !rounded-xl'
          loading={isEditingContract || isEditingMismatch}
          disabled={isEditingContract || isEditingMismatch}
        >
          {t('save-edits')}
        </Button>
      </div>
    </form>
  );
};

export default EditContractForm;
